/**
 * OAuth-popup helper — opens the agent's /api/auth/login?popup=1 in
 * a window.open() and resolves with { ok: true } as soon as the
 * embed can confirm the user is signed in. Four signal channels
 * watched in parallel — first to fire wins:
 *
 *   1. `/api/auth/me` poll       — load-bearing; works regardless
 *                                  of what the popup does.
 *   2. postMessage from the popup-complete page.
 *   3. BroadcastChannel('ordo-agent-auth') — same-origin only.
 *   4. `storage` event for the 'ordo-agent-auth' key — same-origin only.
 *
 * Plus a `popup.closed` poll so users who close the popup before
 * signing in (or whose popup is silently blocked) don't hang
 * forever.
 *
 * Plan 19B / 20 (B.fix.1 + B.fix.3).
 *
 * Usage:
 *   const result = await openSigninPopup()
 *   if (result.ok) { /* refetch /api/auth/me, retry failed message * / }
 *
 * Note: BroadcastChannel + storage only fire when the embed is
 * loaded same-origin with the agent (the popup writes to the agent's
 * origin's storage, not the host app's). Cross-origin embed mounts
 * (partner script-tag / different subdomain) recover via the /me
 * poll, which is why that channel is the load-bearing one.
 */

import { getApiBase } from './socket-client'

const POPUP_TIMEOUT_MS = 5 * 60 * 1000
const POPUP_NAME = 'ordo-agent-signin'
const POPUP_FEATURES = 'popup,width=520,height=720,left=200,top=100'
const BROADCAST_CHANNEL_NAME = 'ordo-agent-auth'
const STORAGE_FLAG_KEY = 'ordo-agent-auth'
const ME_POLL_INTERVAL_MS = 1000
const POPUP_CLOSE_POLL_INTERVAL_MS = 500

interface SigninResult {
  ok: boolean
  /** 'success'      = popup-complete posted a success message back.
   *  'broadcast'    = BroadcastChannel signal (same-origin embeds).
   *  'storage'      = storage event signal (same-origin embeds).
   *  'me-detected'  = /me poll confirmed the cookie is valid.
   *  'closed'       = user closed the popup; we re-checked /me and they are authed.
   *  'cancelled'    = popup closed without auth.
   *  'blocked'      = popup never opened (browser blocked it).
   *  'timeout'      = no signal in 5 min. */
  reason: 'success' | 'broadcast' | 'storage' | 'me-detected' | 'closed' | 'cancelled' | 'blocked' | 'timeout'
}

export function openSigninPopup(): Promise<SigninResult> {
  if (typeof window === 'undefined') {
    return Promise.resolve({ ok: false, reason: 'blocked' })
  }
  const base = getApiBase()
  const href = `${base || ''}/api/auth/login?popup=1`

  return new Promise<SigninResult>((resolve) => {
    const popup = window.open(href, POPUP_NAME, POPUP_FEATURES)
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      resolve({ ok: false, reason: 'blocked' })
      return
    }

    let settled = false
    let bc: BroadcastChannel | null = null
    const closePopup = () => {
      try {
        popup.close()
      } catch {
        /* opener can't always close cross-origin popups; that's fine */
      }
    }
    const settle = (result: SigninResult) => {
      if (settled) {
        return
      }
      settled = true
      window.removeEventListener('message', onMessage)
      window.removeEventListener('storage', onStorage)
      window.clearInterval(closePoll)
      window.clearInterval(mePoll)
      window.clearTimeout(deadline)
      bc?.close()
      resolve(result)
    }

    const onMessage = (ev: MessageEvent) => {
      // Origin filter — only trust messages from the configured agent
      // base. When base is empty (same-origin embed), the agent posts
      // from the same window.location.origin so allow it. Anything
      // else gets ignored.
      const expected = base ? new URL(base).origin : window.location.origin
      if (ev.origin !== expected) {
        return
      }
      const data = ev.data as { type?: string; ok?: boolean } | null
      if (!data || data.type !== 'ordo-agent-auth') {
        return
      }
      if (data.ok) {
        closePopup()
        settle({ ok: true, reason: 'success' })
      }
    }

    const onStorage = (ev: StorageEvent) => {
      if (ev.key === STORAGE_FLAG_KEY && ev.newValue) {
        closePopup()
        settle({ ok: true, reason: 'storage' })
      }
    }

    // /me poll — the load-bearing channel. Source of truth is "does
    // the cookie validate now". Don't trust the popup to close itself
    // or to deliver a postMessage — sometimes neither happens (Chrome
    // blocks close after cross-origin nav, multipass eats the next
    // query, etc). Polling /me catches every signed-in case.
    const mePoll = window.setInterval(async () => {
      try {
        const r = await fetch(`${base || ''}/api/auth/me`, { credentials: 'include' })
        if (r.ok) {
          closePopup()
          settle({ ok: true, reason: 'me-detected' })
        }
      } catch {
        /* network blip; try again next tick */
      }
    }, ME_POLL_INTERVAL_MS)

    // Popup-closed fallback. Catches "user closed without signing in"
    // — re-checks /me to handle the edge case where postMessage was
    // lost but the cookie did get written.
    const closePoll = window.setInterval(async () => {
      if (!popup.closed) {
        return
      }
      try {
        const r = await fetch(`${base || ''}/api/auth/me`, { credentials: 'include' })
        settle({ ok: r.ok, reason: r.ok ? 'closed' : 'cancelled' })
      } catch {
        settle({ ok: false, reason: 'cancelled' })
      }
    }, POPUP_CLOSE_POLL_INTERVAL_MS)

    const deadline = window.setTimeout(() => settle({ ok: false, reason: 'timeout' }), POPUP_TIMEOUT_MS)

    window.addEventListener('message', onMessage)
    window.addEventListener('storage', onStorage)
    try {
      bc = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
      bc.onmessage = (ev) => {
        const data = ev.data as { type?: string; ok?: boolean } | null
        if (data?.type === 'ordo-agent-auth' && data.ok) {
          closePopup()
          settle({ ok: true, reason: 'broadcast' })
        }
      }
    } catch {
      /* BroadcastChannel unsupported (older browsers / SSR). Skip — the
         /me poll covers the same case. */
    }
  })
}
